// Single source of truth for every price shown in the document scanning
// section. Change a number here and every page follows.
//
// The two headline rates below are the owner's, set 29 Oct 2026. Everything
// else is deliberately "quoted" rather than a published number, because the
// cost is driven by how much hand-handling the paper needs and we have no
// supplier prices yet for the work we would subcontract (large format,
// microfiche, bound volumes).
//
// Collection policy, set 29 Oct 2026: pickup and return are chargeable, and
// so is the handling time either end. Dropping boxes to us is free. That is
// why no rate below claims to include collection.
//
// Market context for whoever revisits this: a standard archive box runs
// $150-$350 across the Australian market, the nearest Gold Coast competitor
// quotes $100-$300, and bulk loose A4 bottoms out around $0.07-$0.15 a page.
// See ScanningPage/COMPETITOR-PRICING-2026-10-29.md.

export const BOX_PAGES = 2000;

export const PER_BOX_FROM = "$100";

// The published hourly rate. There is a lower rate for clients booking roughly
// 8-16 hours a week, but it is deliberately NOT advertised: those clients are
// meant to ask. Keep it out of the pages and out of the meta descriptions.
export const HOURLY_RATE = "$60";

// Optional AI usage, set 30 Oct 2026. Seymour's measured Haiku 5.5 runs
// cost about US$0.38 per 1,000 non-blank pages. Twice that cost, converted
// to AUD and rounded up, gives this starting rate. Confirm each job on a sample.
export const AI_TAGGING_FROM = "$1.20";
export const AI_TAGGING_UNIT = "1,000 pages";
export const aiTaggingPrice = `from ${AI_TAGGING_FROM} per ${AI_TAGGING_UNIT}`;

export const MINIMUM_WEEKLY = "5 hours";
export const CONTRACT_MONTHS = 6;

// How we invite the volume conversation without naming a number.
export const volumeHint =
  "Booking more than a day a week? Talk to us: regular volume is priced differently.";

export const minimumWeeklyText = MINIMUM_WEEKLY
  ? `a minimum of ${MINIMUM_WEEKLY} booked each week`
  : "a minimum booked each week";

export const contractText = CONTRACT_MONTHS
  ? `contracts of ${CONTRACT_MONTHS} months or longer`
  : "longer term contracts";

// What the per-box rate assumes. Anything outside this is quoted.
export const boxConditions = [
  "Loose paper that feeds through a document scanner",
  `Up to ${BOX_PAGES.toLocaleString()} A4 pages in a standard archive box`,
  "No staples, clips, bindings or pockets",
  "Nothing torn, folded, glued or stuck down",
  "Single or double sided, colour or black and white",
];

// Priced after we have seen it, because the cost is hand-handling time.
export const quotedItems = [
  {
    item: "Stapled, clipped or bound bundles",
    why: "Every fastener comes out by hand before a page can be fed",
  },
  {
    item: "Books, ledgers and minute books",
    why: "Scanned on a flatbed a page at a time, or guillotined if you allow it",
  },
  {
    item: "Torn, folded or fragile paper",
    why: "Repaired and flattened, or sleeved and fed in a carrier",
  },
  {
    item: "Faded thermal, fax and carbon paper",
    why: "Needs slower scanning and per-page checking to stay readable",
  },
  {
    item: "Plans and drawings, A2 to A0",
    why: "Wide format equipment, and rolled sheets need relaxing flat first",
  },
  {
    item: "Microfiche and microfilm",
    why: "Specialist equipment",
  },
  {
    item: "Photos, slides and negatives",
    why: "Handled individually rather than fed",
  },
];

// Standard scanning inclusions. AI naming and metadata extraction are optional.
export const included = [
  {
    item: "OCR, so every page is searchable",
    elsewhere: "$0.02 - $0.08 per page",
  },
  {
    item: "Blank and duplicate pages removed",
    elsewhere: "charged as scanned pages",
  },
  {
    item: "PDF output grouped as agreed",
    elsewhere: "rarely offered",
  },
  {
    item: "Delivery to SharePoint, a drive, your app or a USB",
    elsewhere: "USB or download only",
  },
  { item: "Audit report of every file we handled", elsewhere: "on request" },
];

// Operational extras. Deliberately unpriced until the owner sets them.
export const addOns = [
  { item: "Optional AI document naming and metadata tagging", price: aiTaggingPrice },
  { item: "Drop your boxes to us", price: "Free" },
  {
    item: "Pickup from you, Gold Coast and Brisbane",
    price: "Charged, including handling time",
  },
  {
    item: "Return of your originals to you",
    price: "Charged, including handling time",
  },
  { item: "Secure shredding after scanning", price: "Quoted" },
  { item: "Storage while you decide", price: "Quoted" },
  { item: "Rush turnaround", price: "Quoted" },
  {
    item: "Integration into your CRM, or a cloud document portal we build",
    price: "Free on longer term contracts",
  },
  { item: "Custom AI tagging flow for your document types", price: "Quoted" },
];
